import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Segmented, Select } from 'antd';
import dayjs from 'dayjs';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { Briefcase, Clock, FileText, Handshake, Plus, UserCheck } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import {
  SOURCE, STAGE, branchById, daysBetween, fmtD, pct, positionById, reqCandidates, reqHired, reqOpenSlots, stageAt, toDay,
  needsSchedule, pendingInterview, reqById,
} from './hrLogic';
import { interviewWhen } from './RecruitPanels';
import { earlyAttrition, probationStats, recruitMetrics, sourceStats, stageDurations } from './hrInsights';
import { Kpi, PageHeader, Panel, candUrl, reqUrl, useHrPerms } from './hrUi';

const PERIODS = { '30': 30, '90': 90, '365': 365 } as const;

/** Tổng quan tuyển dụng: phễu, thời gian tuyển, nút thắt, chất lượng nguồn, yêu cầu đang mở */
export default function RecruitDashboard() {
  const s = useHr();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canRecruit } = useHrPerms();
  const today = toDay();
  const now = Date.now();
  const period = (params.get('period') as keyof typeof PERIODS) || '90';
  const days = PERIODS[period] || 90;
  const branchId = params.get('branch') || undefined;
  const sc = { branchId };
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const from = dayjs().subtract(days, 'day').valueOf();

  const d = useMemo(() => ({
    rec: recruitMetrics(s, from, now, sc),
    dur: stageDurations(s, from, now, sc),
    sources: sourceStats(s, today, sc),
    early: earlyAttrition(s, today, sc),
    prob: probationStats(s, today, sc),
    months: Array.from({ length: 12 }, (_, i) => {
      const m = dayjs().subtract(11 - i, 'month');
      const a = m.startOf('month').valueOf();
      const b = m.endOf('month').valueOf();
      const inScope = (reqId: string) => { const r = s.requisitions.find(x => x.id === reqId); return !!r && (!branchId || r.branchId === branchId); };
      return {
        month: m.format('MM/YY'),
        applied: s.candidates.filter(c => inScope(c.requisitionId) && c.appliedAt >= a && c.appliedAt <= b).length,
        hired: s.candidates.filter(c => inScope(c.requisitionId) && c.outcome === 'hired' && (stageAt(c, 'hired') || 0) >= a && (stageAt(c, 'hired') || 0) <= b).length,
      };
    }),
  }), [s, from, branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const { rec, dur, sources, early, prob, months } = d;

  const open = s.requisitions.filter(r => r.status === 'approved' && (!branchId || r.branchId === branchId)).sort((a, b) => a.deadline.localeCompare(b.deadline));
  const pending = s.requisitions.filter(r => r.status === 'pending' && (!branchId || r.branchId === branchId));
  const slots = open.reduce((a, r) => a + reqOpenSlots(s, r), 0);
  const maxFunnel = Math.max(1, rec.funnel[0]?.n || 0);
  // Offer → nhận việc phụ thuộc ngày ứng viên nghỉ chỗ cũ nên không tính là nút thắt
  const slowest = dur.slice(0, 4).filter(x => x.days !== null).sort((a, b) => (b.days || 0) - (a.days || 0))[0];
  const maxReason = Math.max(1, ...rec.rejectReasons.map(x => x.n), ...rec.withdrawReasons.map(x => x.n));
  // Lịch phỏng vấn 7 ngày tới + lịch đã quá giờ chưa ghi kết quả + ứng viên ở vòng phỏng vấn chưa được hẹn
  const inBranch = (reqId: string) => !branchId || reqById(s, reqId)?.branchId === branchId;
  const weekEnd = dayjs().add(7, 'day').endOf('day').valueOf();
  const agenda = s.candidates.filter(c => inBranch(c.requisitionId))
    .map(c => ({ c, iv: pendingInterview(c) }))
    .filter((x): x is { c: typeof x.c; iv: NonNullable<typeof x.iv> } => !!x.iv && x.iv.at <= weekEnd)
    .sort((a, b) => a.iv.at - b.iv.at);
  const unscheduled = s.candidates.filter(c => inBranch(c.requisitionId) && needsSchedule(c));

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Tổng quan tuyển dụng" desc={`${days} ngày qua · ${branchId ? branchById(s, branchId)?.name : 'tất cả chi nhánh'}`}
          extra={<>
            <Segmented value={period} onChange={v => go({ period: v === '90' ? null : String(v) })} options={[{ value: '30', label: '30 ngày' }, { value: '90', label: '90 ngày' }, { value: '365', label: '12 tháng' }]} />
            <Select className="w-[180px]" value={branchId || ''} onChange={v => go({ branch: v || null })} options={[{ value: '', label: 'Tất cả chi nhánh' }, ...s.branches.map(b => ({ value: b.id, label: b.name }))]} />
            {canRecruit && <Button type="primary" icon={<Plus size={14} />} onClick={() => navigate('/jobs-view?new=1')}>Yêu cầu tuyển dụng</Button>}
          </>} />

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Kpi icon={<Briefcase size={18} />} tone="bg-indigo-50 text-indigo-600" label="Vị trí đang tuyển" value={slots} hint={`${open.length} yêu cầu · ${pending.length} chờ duyệt`} onClick={() => navigate('/jobs-view')} />
          <Kpi icon={<FileText size={18} />} tone="bg-sky-50 text-sky-600" label="Hồ sơ mới" value={rec.applied} hint={`${(rec.applied / Math.max(1, days / 7)).toFixed(1)} hồ sơ / tuần`} onClick={() => navigate('/candidates-view')} />
          <Kpi icon={<UserCheck size={18} />} tone="bg-emerald-50 text-emerald-600" label="Tuyển được" value={rec.hired} hint={rec.applied ? `${pct(rec.hired, rec.applied)}% hồ sơ` : undefined} />
          <Kpi icon={<Clock size={18} />} tone="bg-amber-50 text-amber-600" label="Thời gian tuyển TB" value={rec.timeToFill !== null ? `${rec.timeToFill} ngày` : '—'} hint={rec.timeToHire !== null ? `từ lúc nộp hồ sơ: ${rec.timeToHire} ngày` : undefined} />
          <Kpi icon={<Handshake size={18} />} tone="bg-violet-50 text-violet-600" label="Tỷ lệ nhận offer" value={rec.offerRate !== null ? `${rec.offerRate}%` : '—'} hint={`${rec.offers} offer có kết quả`} />
          <Kpi icon={<UserCheck size={18} />} tone={early.rate > 15 ? 'bg-rose-50 text-rose-600' : 'bg-teal-50 text-teal-600'} label="Chất lượng tuyển (12 tháng)" value={prob.pass + prob.fail ? `${prob.rate}%` : '—'}
            hint={`qua thử việc · nghỉ <90 ngày: ${early.early}/${early.cohort}`} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          <Panel title="Phễu tuyển dụng" extra={slowest?.days ? <span className="text-[11px] text-amber-600">Chậm nhất: {STAGE[slowest.stage].label} → bước sau ({slowest.days} ngày)</span> : undefined}>
            <div className="p-4 space-y-2">
              {rec.funnel.map((f, i) => (
                <div key={f.stage} className="flex items-center gap-3 text-xs">
                  <div className="w-[170px] shrink-0 text-gray-600 dark:text-gray-300">{STAGE[f.stage].label}</div>
                  <div className="flex-1 h-6 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div className="h-full rounded flex items-center px-2 text-white font-semibold" style={{ width: `${Math.max(6, (f.n / maxFunnel) * 100)}%`, background: STAGE[f.stage].color }}>{f.n}</div>
                  </div>
                  <div className="w-[120px] text-right text-gray-500">
                    {i > 0 && rec.funnel[i - 1].n ? `${pct(f.n, rec.funnel[i - 1].n)}% qua bước` : ''}
                    {dur[i - 1]?.days !== null && i > 0 ? <div className="text-[10px] text-gray-400">~{dur[i - 1].days} ngày</div> : null}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Hồ sơ và tuyển được theo tháng">
            <div className="h-[260px] p-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={months} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="applied" name="Hồ sơ" fill="#93c5fd" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="hired" name="Tuyển được" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <Panel title={`Lịch phỏng vấn 7 ngày tới (${agenda.length})`}
          extra={<span className="flex items-center gap-3 text-[11px]">
            {unscheduled.length > 0 && <button type="button" className="text-amber-600 hover:underline" onClick={() => navigate('/interviews')}>{unscheduled.length} ứng viên chưa được hẹn</button>}
            <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => navigate('/interviews')}>Xem lịch tuần ›</button>
          </span>}>
          {agenda.length === 0 ? <div className="px-4 py-6 text-sm text-gray-400">Chưa có lịch phỏng vấn</div> : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {agenda.map(({ c, iv }) => {
                const w = interviewWhen(iv);
                const r = reqById(s, c.requisitionId);
                return (
                  <button key={iv.id} type="button" onClick={() => navigate(candUrl(c.id))} className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/40 grid grid-cols-[130px_1fr] sm:grid-cols-[150px_1fr_1fr] gap-x-3 gap-y-0.5 text-sm">
                    <span className={`font-semibold ${w.tone}`}>{w.label}{w.overdue && <span className="block text-[11px] font-normal">chưa ghi kết quả</span>}</span>
                    <span className="min-w-0"><span className="font-medium">{c.name}</span><span className="block text-[11px] text-gray-400 truncate">{STAGE[iv.stage].label} · {positionById(s, r?.positionId)?.name} · {branchById(s, r?.branchId || '')?.short}</span></span>
                    <span className="min-w-0 text-xs text-gray-500 col-start-2 sm:col-start-auto truncate">{iv.interviewer}<span className="block text-gray-400 truncate">{iv.place}</span></span>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel title={`Yêu cầu đang tuyển (${open.length})`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-xs text-gray-500 text-left"><th className="font-medium px-4 py-2">Vị trí</th><th className="font-medium">Tiến độ</th><th className="font-medium">Ứng viên đang xử lý</th><th className="font-medium">Đang ở bước offer</th><th className="font-medium">Hạn</th></tr></thead>
              <tbody>
                {open.map(r => {
                  const cands = reqCandidates(s, r.id).filter(c => c.outcome === 'open');
                  const left = daysBetween(today, r.deadline);
                  return (
                    <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer" onClick={() => navigate(reqUrl(r.id))}>
                      <td className="px-4 py-2"><div className="font-medium">{positionById(s, r.positionId)?.name}</div><div className="text-[11px] text-gray-400">{r.code} · {branchById(s, r.branchId)?.short}</div></td>
                      <td>{reqHired(s, r)}/{r.qty}</td>
                      <td>{cands.length}</td>
                      <td>{cands.filter(c => c.stage === 'offer').length}</td>
                      <td className={left < 0 ? 'text-rose-600 font-medium' : left <= 7 ? 'text-amber-600' : ''}>{fmtD(r.deadline)} <span className="text-[11px]">({left < 0 ? `trễ ${-left}` : `còn ${left}`} ngày)</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4 items-start">
          <Panel title="Chất lượng nguồn tuyển (12 tháng)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="text-xs text-gray-500 text-left"><th className="font-medium px-4 py-2">Nguồn</th><th className="font-medium text-right">Hồ sơ</th><th className="font-medium text-right">Vào PV</th><th className="font-medium text-right">Tuyển</th><th className="font-medium text-right">Qua thử việc</th><th className="font-medium text-right px-4">Nghỉ &lt;90 ngày</th></tr></thead>
                <tbody>
                  {sources.map(r => (
                    <tr key={r.source} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-4 py-2">{SOURCE[r.source]}</td>
                      <td className="text-right">{r.applied}</td>
                      <td className="text-right">{r.interviewed} <span className="text-[11px] text-gray-400">({pct(r.interviewed, r.applied)}%)</span></td>
                      <td className="text-right">{r.hired} <span className="text-[11px] text-gray-400">({r.hireRate}%)</span></td>
                      <td className="text-right">{r.evaluated ? `${r.passed}/${r.evaluated}` : '—'}</td>
                      <td className={`text-right px-4 ${r.early ? 'text-rose-600 font-medium' : 'text-gray-400'}`}>{r.early || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 pb-3 text-[11px] text-gray-400">Nguồn tốt là nguồn có người qua thử việc và ở lại, không chỉ nhiều hồ sơ.</div>
          </Panel>
          <Panel title="Lý do loại / ứng viên từ chối">
            <div className="p-4 space-y-3">
              {[['Công ty loại', rec.rejectReasons, '#f43f5e'], ['Ứng viên rút / từ chối', rec.withdrawReasons, '#94a3b8']].map(([title, rows, color]) => (
                <div key={title as string}>
                  <div className="text-[11px] text-gray-500 mb-1">{title as string}</div>
                  <div className="space-y-1.5">
                    {(rows as { reason: string; n: number }[]).map(x => (
                      <div key={x.reason} className="flex items-center gap-2 text-xs">
                        <div className="w-[150px] shrink-0 truncate text-gray-600 dark:text-gray-300">{x.reason}</div>
                        <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(x.n / maxReason) * 100}%`, background: color as string }} /></div>
                        <div className="w-6 text-right">{x.n}</div>
                      </div>
                    ))}
                    {!(rows as unknown[]).length && <div className="text-xs text-gray-400">Chưa có</div>}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </Layout>
  );
}
